import { useMessage } from '@messageformat/react';
import { ReactElement } from 'react';
import { font, space } from '@op/opds.tokens';
import { parseTextContent } from '../util/parseTextContent';
import { HeadingType } from '../types';

interface Props {
  messageId: string;
  twoColumns: boolean;
  headingAs?: HeadingType;
}

const formatBoldText = (text: string | undefined): ReactElement | string | null => {
  if (!text) return null;

  const plainText = text.startsWith('%') ? text.slice(1) : text;
  const boldEndIndex = plainText.lastIndexOf('$');
  const hasBoldPart = boldEndIndex !== -1;

  if (hasBoldPart) {
    return (
      <>
        <span
          style={{
            fontWeight: font.weight.bold,
          }}
        >
          {plainText.substring(0, boldEndIndex)}
        </span>
        {plainText.slice(boldEndIndex + 1)}
      </>
    );
  }

  return plainText;
};

const renderList = (
  items: string[] | undefined,
  listType: 'ordered' | 'unordered',
  key: string,
): ReactElement => {
  const listStyleType = listType === 'ordered' ? 'decimal' : 'disc';
  const ListTag = listType === 'ordered' ? 'ol' : 'ul';

  return (
    <ListTag key={key} className="investoracademy-bullet-list-array">
      {items?.map((item: string) => (
        <li key={item} style={{ listStyleType }}>
          {formatBoldText(item)}
        </li>
      ))}
    </ListTag>
  );
};

const TextComponent = (props: Props): ReactElement => {
  const { messageId, twoColumns, headingAs: Heading = 'h4' } = props;
  const textBody = useMessage(messageId);
  const parsedTextBody = parseTextContent(textBody);
  let orderedListIndex = 0;
  let unorderedListIndex = 0;

  return (
    <div className="investoracademy-page-content">
      <div className={twoColumns ? 'investoracademy-text-content' : ''}>
        {parsedTextBody.map((value) => {
          if (value.type === 'subtitle') {
            return (
              <Heading
                key={`${value.text}`}
                className={
                  ['h5', 'h6'].includes(Heading)
                    ? 'opds-styles-v1-text-title-small-strong'
                    : 'opds-styles-v1-text-title-large'
                }
                style={{ marginBottom: space.small }}
              >
                {value.text}
              </Heading>
            );
          }

          if (value.type === 'ordered-list') {
            orderedListIndex += 1;
            return renderList(value.items, 'ordered', `orderedList${orderedListIndex}`);
          }

          if (value.type === 'unordered-list') {
            unorderedListIndex += 1;
            return renderList(
              value.items,
              'unordered',
              `unorderedList${unorderedListIndex}`,
            );
          }

          return (
            <p
              key={`${value.text}`}
              className="investoracademy-text-paragraph"
              style={{
                paddingBottom: value.text?.startsWith('%') ? 0 : undefined,
              }}
            >
              {formatBoldText(value.text)}
            </p>
          );
        })}
      </div>
    </div>
  );
};

export default TextComponent;
